import { useCallback, useMemo } from 'react'
import { useSearchParams } from 'react-router-dom'
import {
  ImportedQuestionnaireLocation,
  resolveImportedLocation,
  storeImportedLocation,
} from './questionnaireLocation'
import { useDropQuestionFilters } from './questionnaire.utils'
import { QuestionnaireQuestion } from './QuestionnaireTypes'

export const QUESTIONNAIRE_SIDEBAR_TAB_PARAM = 'tab'
// Deliberately the same name the vendor portal already uses for its own
// section param, so one questionnaire URL means the same thing everywhere.
export const QUESTIONNAIRE_SECTION_PARAM = 'section'
export const QUESTIONNAIRE_PANE_PARAM = 'pane'
export const QUESTIONNAIRE_COMMENT_SECTION_PARAM = 'comment_section'
export const QUESTIONNAIRE_COMMENT_QUESTION_PARAM = 'comment_question'
// A question to bring into view on arrival, within the section param's
// section: where a link to one question lands. Scrolling only -- unlike
// comment_question it selects nothing, so it is dropped once used.
export const QUESTIONNAIRE_QUESTION_PARAM = 'question'

// The left panel. Comments used to be one of these and is now a rail pane.
//
// Overview and Versions belong to the questionnaire rather than to the answering
// view, so their bodies are built by the show page -- but they are shown in this
// panel beside the sections and attachments, and so are named here with them:
// the panel has one strip, and one param saying which of it is showing.
export const QUESTIONNAIRE_SIDEBAR_TAB = {
  OVERVIEW: 'overview',
  SECTIONS: 'sections',
  ATTACHMENTS: 'attachments',
  VERSIONS: 'versions',
} as const

// The right rail holds this, or nothing at all. Trustero Intelligence is
// deliberately NOT a value here: it opens as the app's own full-height panel
// rather than taking turns with comments in this column.
export const QUESTIONNAIRE_RAIL_PANE = {
  COMMENTS: 'comments',
} as const

const VALID_TABS: string[] = Object.values(QUESTIONNAIRE_SIDEBAR_TAB)
const VALID_PANES: string[] = Object.values(QUESTIONNAIRE_RAIL_PANE)

// A 1-based coordinate from the URL, or undefined for anything that is not one.
const coordinate = (raw: string): number | undefined => {
  const parsed = Number(raw)
  return raw !== '' && Number.isInteger(parsed) && parsed > 0
    ? parsed
    : undefined
}

export interface QuestionnaireUrlState {
  // Which sidebar tab is showing. Always one of QUESTIONNAIRE_SIDEBAR_TAB.
  tab: string
  // Which rail pane is showing, or undefined for a hidden rail -- which is the
  // default, so an unparameterised URL opens with the document at full width.
  pane?: string
  // The section the DOCUMENT is showing, by 1-based position. Undefined when
  // the URL does not name one, which callers resolve to their first visible
  // section.
  //
  // A position, not an id: submitting freezes the answered form version and
  // opens a fresh copy, so every section gets a new id on every submit. A link
  // holding an id -- a notification's, or one pasted to a colleague -- stops
  // resolving the moment anyone submits, while a position survives.
  sectionNumber?: number
  // Which comment thread is SELECTED, as its own (section, question) pair.
  //
  // Separate from sectionNumber above, rather than reusing it, because the rail
  // shows every section's threads: a thread can be selected while the document
  // is showing a different section, and selecting one moves the document to it.
  // These were a single overloaded `question` param before, which could not say
  // whether the rail was even open.
  commentSectionNumber?: number
  commentQuestionNumber?: number
  // The question a link asked to land on, by 1-based position in sectionNumber.
  questionNumber?: number
}

// An absent key leaves its param alone; a key present with `undefined` deletes
// it. So { pane: undefined } hides the rail, while { tab: 'sections' } leaves
// the rail exactly as it was.
export interface QuestionnaireUrlUpdate {
  tab?: string
  pane?: string
  sectionNumber?: number
  commentSectionNumber?: number
  commentQuestionNumber?: number
  questionNumber?: number
  // Clears the question filters in the same write: a jump to a question they
  // hide (useQuestionFilter's isFilteredOut). Given the question itself, the
  // view is set to the one it is in, so a jump to a pre-answered question
  // lands.
  clearQuestionFilters?: boolean | QuestionnaireQuestion
}

// Where the answering view keeps what you are looking at: the sidebar tab, the
// document's section, the rail pane, and the selected comment thread. A reload
// -- or a link pasted to a colleague -- comes back to the same place.
//
// One hook returning one setter, rather than a setter per param, because
// setSearchParams is a navigate: two calls in the same tick both compute from
// the current location and the last one wins. Selecting a thread in another
// section sets the pane, the document's section AND the coordinate, so those
// have to land in a single write.
//
// Replace, not push: none of this is a place you navigated to, and pushing
// would make Back walk every pane and thread you touched before leaving.
//
// Shared by every surface with this layout -- both answering views and the
// customer's read-only one.
export const useQuestionnaireUrlState = (
  imported?: ImportedQuestionnaireLocation,
): [QuestionnaireUrlState, (next: QuestionnaireUrlUpdate) => void] => {
  const [searchParams, setSearchParams] = useSearchParams()
  const dropFilters = useDropQuestionFilters()

  const rawTab = searchParams.get(QUESTIONNAIRE_SIDEBAR_TAB_PARAM) ?? ''
  const rawPane = searchParams.get(QUESTIONNAIRE_PANE_PARAM) ?? ''
  const rawSection = searchParams.get(QUESTIONNAIRE_SECTION_PARAM) ?? ''
  const rawCommentSection =
    searchParams.get(QUESTIONNAIRE_COMMENT_SECTION_PARAM) ?? ''
  const rawCommentQuestion =
    searchParams.get(QUESTIONNAIRE_COMMENT_QUESTION_PARAM) ?? ''
  const rawQuestion = searchParams.get(QUESTIONNAIRE_QUESTION_PARAM) ?? ''

  const state = useMemo(
    (): QuestionnaireUrlState => ({
      // An unrecognised tab falls back rather than rendering an empty sidebar.
      // An unrecognised pane reads as "no pane", which is a hidden rail -- the
      // default -- rather than an empty column.
      tab: VALID_TABS.includes(rawTab)
        ? rawTab
        : QUESTIONNAIRE_SIDEBAR_TAB.SECTIONS,
      pane: VALID_PANES.includes(rawPane) ? rawPane : undefined,
      sectionNumber: coordinate(rawSection),
      commentSectionNumber: coordinate(rawCommentSection),
      commentQuestionNumber: coordinate(rawCommentQuestion),
      questionNumber: coordinate(rawQuestion),
    }),
    [
      rawTab,
      rawPane,
      rawSection,
      rawCommentSection,
      rawCommentQuestion,
      rawQuestion,
    ],
  )

  const resolvedState = useMemo(
    () =>
      imported ? resolveImportedLocation(searchParams, state, imported) : state,
    [imported, searchParams, state],
  )

  const setUrlState = useCallback(
    (next: QuestionnaireUrlUpdate) => {
      setSearchParams(
        (current) => {
          // Built off the live params rather than a captured copy, so this
          // leaves anything else in the query string alone.
          const params = new URLSearchParams(current)

          const put = (key: string, value: string | number | undefined) => {
            if (value === undefined) {
              params.delete(key)
            } else {
              params.set(key, String(value))
            }
          }

          if ('tab' in next) {
            // An unknown tab is dropped rather than written: the reader would
            // otherwise be left on a sidebar with nothing in it.
            if (next.tab !== undefined && VALID_TABS.includes(next.tab)) {
              params.set(QUESTIONNAIRE_SIDEBAR_TAB_PARAM, next.tab)
            }
          }
          if ('pane' in next) {
            if (next.pane === undefined) {
              params.delete(QUESTIONNAIRE_PANE_PARAM)
            } else if (VALID_PANES.includes(next.pane)) {
              params.set(QUESTIONNAIRE_PANE_PARAM, next.pane)
            }
          }
          if ('sectionNumber' in next) {
            put(QUESTIONNAIRE_SECTION_PARAM, next.sectionNumber)
          }
          if ('commentSectionNumber' in next) {
            put(QUESTIONNAIRE_COMMENT_SECTION_PARAM, next.commentSectionNumber)
          }
          if ('commentQuestionNumber' in next) {
            put(
              QUESTIONNAIRE_COMMENT_QUESTION_PARAM,
              next.commentQuestionNumber,
            )
          }
          if ('questionNumber' in next) {
            put(QUESTIONNAIRE_QUESTION_PARAM, next.questionNumber)
          }
          if (next.clearQuestionFilters) {
            dropFilters(
              params,
              typeof next.clearQuestionFilters === 'object'
                ? next.clearQuestionFilters
                : undefined,
            )
          }

          if (imported) {
            storeImportedLocation(
              params,
              {
                ...resolveImportedLocation(
                  current,
                  {
                    ...resolvedState,
                    sectionNumber: coordinate(
                      current.get(QUESTIONNAIRE_SECTION_PARAM) ?? '',
                    ),
                    commentSectionNumber: coordinate(
                      current.get(QUESTIONNAIRE_COMMENT_SECTION_PARAM) ?? '',
                    ),
                    commentQuestionNumber: coordinate(
                      current.get(QUESTIONNAIRE_COMMENT_QUESTION_PARAM) ?? '',
                    ),
                  },
                  imported,
                ),
                ...next,
              },
              imported,
            )
            if (
              'commentSectionNumber' in next ||
              'commentQuestionNumber' in next
            )
              params.delete('comment_id')
          }
          return params
        },
        { replace: true },
      )
    },
    [setSearchParams, imported, resolvedState, dropFilters],
  )

  return [resolvedState, setUrlState]
}
